import { useCallback } from 'react';

const SNILS_DIGITS_COUNT = 11;

const NON_DIGITS_REGEX = /\D/g;
const SNILS_GROUPS_REGEX = /^(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})$/;

const SNILS_MIN_NUMBER_WITHOUT_CONTROL_CHECK = 1001998;

const SNILS_ERROR_MESSAGES = {
  digitsCount: 'Введите 11 цифр СНИЛС',
  format: 'Неверный формат СНИЛС. Используйте шаблон: XXX-XXX-XXX XX',
  controlSum:
    'Проверьте правильность ввода номера (ошибка в контрольном числе)',
} as const;

type SnilsValidationError =
  (typeof SNILS_ERROR_MESSAGES)[keyof typeof SNILS_ERROR_MESSAGES];

type SnilsValidationResult = SnilsValidationError | null;

const getSnilsDigits = (value: string): string => {
  return value.replace(NON_DIGITS_REGEX, '').slice(0, SNILS_DIGITS_COUNT);
};

const formatSnils = (value: string): string => {
  const digits = getSnilsDigits(value);
  const match = SNILS_GROUPS_REGEX.exec(digits);

  if (!match) {
    return '';
  }

  const [, firstGroup, secondGroup, thirdGroup, controlGroup] = match;

  const mainPart = [firstGroup, secondGroup, thirdGroup]
    .filter(Boolean)
    .join('-');

  return controlGroup ? `${mainPart} ${controlGroup}` : mainPart;
};

const calculateSnilsControlNumber = (digits: string): number => {
  const sum = digits
    .slice(0, 9)
    .split('')
    .reduce((accumulator, digit, index) => {
      return accumulator + Number(digit) * (9 - index);
    }, 0);

  if (sum < 100) {
    return sum;
  }

  const modulo = sum % 101;

  return modulo === 100 ? 0 : modulo;
};

const isSnilsValid = (digits: string): boolean => {
  if (digits.length !== SNILS_DIGITS_COUNT) {
    return false;
  }

  const numberWithoutControl = Number(digits.slice(0, 9));

  if (numberWithoutControl <= SNILS_MIN_NUMBER_WITHOUT_CONTROL_CHECK) {
    return true;
  }

  const expectedControlNumber = calculateSnilsControlNumber(digits);
  const actualControlNumber = Number(digits.slice(9, 11));

  return expectedControlNumber === actualControlNumber;
};

export const useSnils = () => {
  const validateSnils = useCallback((value: string): SnilsValidationResult => {
    const digits = getSnilsDigits(value);

    if (digits.length !== SNILS_DIGITS_COUNT) {
      return SNILS_ERROR_MESSAGES.digitsCount;
    }

    if (!isSnilsValid(digits)) {
      return SNILS_ERROR_MESSAGES.controlSum;
    }

    return null;
  }, []);

  return {
    validateSnils,
    formatSnils,
    getSnilsDigits,
  };
};
